import React, { ReactNode, useState, useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Link,
  useColorModeValue,
  IconButton,
  Tooltip,
  Collapse,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FiHome, FiShoppingBag, FiDollarSign, FiCreditCard, FiSettings, FiTag, FiChevronLeft, FiChevronRight, FiMail, FiChevronDown, FiChevronUp, FiDatabase, FiTruck, FiPackage, FiClipboard, FiPrinter, FiBell, FiMonitor, FiClock, FiInfo, FiImage } from "react-icons/fi";
import { IconType } from "react-icons";
import UserProfileMenu from "./UserProfileMenu";
import { useStationApprovals } from "../stores/StationApprovalAlerts";

interface SidebarProps {
  children: ReactNode;
  isLoading?: boolean;
}

interface MenuItem {
  path?: string;
  label: string;
  icon: IconType;
  children?: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ children, isLoading = false }) => {
  const location = useLocation();
  const { pending, bannerItems, dismissBanner } = useStationApprovals();
  const pendingCount = pending.length;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<{ [key: string]: boolean }>({
    system: true, // System menu expanded by default
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const bgSidebar = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("#d5e4ea", "gray.700");
  const activeBg = useColorModeValue("#e7f2f6", "blue.900");
  const activeColor = useColorModeValue("#023047", "blue.200");
  const canvasBg = useColorModeValue("#f4fafc", "gray.900");

  // Full-screen mode detection
  useEffect(() => {
    const checkFullscreen = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Check initial state
    checkFullscreen();

    // Listen for fullscreen changes
    document.addEventListener("fullscreenchange", checkFullscreen);
    document.addEventListener("webkitfullscreenchange", checkFullscreen);
    document.addEventListener("mozfullscreenchange", checkFullscreen);
    document.addEventListener("MSFullscreenChange", checkFullscreen);

    return () => {
      document.removeEventListener("fullscreenchange", checkFullscreen);
      document.removeEventListener("webkitfullscreenchange", checkFullscreen);
      document.removeEventListener("mozfullscreenchange", checkFullscreen);
      document.removeEventListener("MSFullscreenChange", checkFullscreen);
    };
  }, []);

  // Auto-collapse sidebar in fullscreen mode
  useEffect(() => {
    if (isFullscreen && !isCollapsed) {
      setIsCollapsed(true);
    }
  }, [isFullscreen]);

  const menuItems: MenuItem[] = [
    { path: "/dashboard", label: "Dashboard", icon: FiHome },
    { path: "/restaurants", label: "Stores", icon: FiShoppingBag },
    { path: "/notices", label: "Notices", icon: FiBell },
    { path: "/subscriptions", label: "Subscriptions", icon: FiCreditCard },
    { path: "/plans", label: "Plans & Pricing", icon: FiTag },
    { path: "/email-templates", label: "Email Templates", icon: FiMail },
    { path: "/pos-receipt-email", label: "POS Receipt Email", icon: FiMail },
    {
      label: "System",
      icon: FiSettings,
      children: [
        { path: "/logo-settings", label: "Logo setting", icon: FiImage },
        { path: "/products", label: "Version", icon: FiPackage },
        { path: "/stripe-settings", label: "Stripe Settings", icon: FiSettings },
        { path: "/eat-printer", label: "Eat Printer", icon: FiPrinter },
        { path: "/google-settings", label: "Google Settings", icon: FiSettings },
        { path: "/email-settings", label: "Email Settings", icon: FiMail },
        { path: "/cloudflare-settings", label: "Cloudflare Settings", icon: FiSettings },
        { path: "/server-certificate", label: "Server certificate", icon: FiSettings },
        { path: "/forbidden-subdomains", label: "Forbidden Subdomains", icon: FiSettings },
        { path: "/taxes", label: "Taxes", icon: FiDollarSign },
        { path: "/color-palettes", label: "Color Palettes", icon: FiTag },
        { path: "/pos-window", label: "POS window", icon: FiMonitor },
        { path: "/permanent-time", label: "Permanent time", icon: FiClock },
        { path: "/payment-processor-settings", label: "Payment Processor Settings", icon: FiSettings },
        { path: "/user-settings", label: "User Settings", icon: FiSettings },
        { path: "/system-log", label: "System Log", icon: FiClipboard },
        { path: "/livro-nuage-settings", label: "Livro Nuage Settings", icon: FiTruck },
        { path: "/api-mapping", label: "API Mapping Tool", icon: FiDatabase },
      ],
    },
    { path: "/pos-information", label: "Information", icon: FiInfo },
    { path: "/finances", label: "Finances", icon: FiDollarSign },
  ];

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isParentActive = (item: MenuItem): boolean => {
    if (item.path) return isActive(item.path);
    if (item.children) {
      return item.children.some(child => child.path && isActive(child.path));
    }
    return false;
  };

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <HStack spacing={0} align="stretch" minH="100vh">
      {/* Sidebar */}
      <Box
        w={isCollapsed ? "60px" : "250px"}
        bg={bgSidebar}
        borderRight="1px"
        borderColor={borderColor}
        p={4}
        position="relative"
        transition="width 0.2s"
      >
        <VStack align="stretch" spacing={1}>
          {!isCollapsed && (
            <HStack spacing={2} mb={2}>
              <Box as="img" src="/red_eagle/red_eagle_32.png" alt="" w="32px" h="32px" />
              <Text fontSize="lg" fontWeight="600" letterSpacing="-0.02em" color="#023047">
                RMS POS Super
              </Text>
            </HStack>
          )}
          {isCollapsed && (
            <Box as="img" src="/red_eagle/red_eagle_32.png" alt="" w="32px" h="32px" mx="auto" mb={2} />
          )}

          {menuItems.map((item) => {
            const IconComponent = item.icon as React.ComponentType<any>;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedMenus[item.label] || false;
            const isItemActive = isParentActive(item);

            if (hasChildren && !isCollapsed) {
              return (
                <Box key={item.label}>
                  <Box
                    display="flex"
                    alignItems="center"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    bg={isItemActive ? activeBg : "transparent"}
                    color={isItemActive ? activeColor : "inherit"}
                    _hover={{ bg: activeBg }}
                    cursor="pointer"
                    onClick={() => toggleMenu(item.label)}
                  >
                    <Box fontSize="20px">
                      {React.createElement(IconComponent)}
                    </Box>
                    <Text ml={3} flex={1}>{item.label}</Text>
                    <Box fontSize="17px">
                      {React.createElement(isExpanded ? FiChevronUp as React.ComponentType<any> : FiChevronDown as React.ComponentType<any>)}
                    </Box>
                  </Box>
                  <Collapse in={isExpanded} animateOpacity>
                    <VStack align="stretch" pl={6} spacing={0} mt={0.5}>
                      {item.children?.map((child) => {
                        const ChildIconComponent = child.icon as React.ComponentType<any>;
                        return (
                          <Link
                            key={child.path}
                            as={RouterLink}
                            to={child.path}
                            display="flex"
                            alignItems="center"
                            px={2}
                            py={1}
                            borderRadius="md"
                            bg={isActive(child.path || "") ? activeBg : "transparent"}
                            color={isActive(child.path || "") ? activeColor : "inherit"}
                            _hover={{ bg: activeBg }}
                          >
                            <Box fontSize="17px">
                              {React.createElement(ChildIconComponent)}
                            </Box>
                            <Text ml={2} fontSize="sm">{child.label}</Text>
                          </Link>
                        );
                      })}
                    </VStack>
                  </Collapse>
                </Box>
              );
            }

            if (hasChildren && isCollapsed) {
              return (
                <Tooltip
                  key={item.label}
                  label={item.label}
                  placement="right"
                  hasArrow
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    bg={isItemActive ? activeBg : "transparent"}
                    color={isItemActive ? activeColor : "inherit"}
                    _hover={{ bg: activeBg }}
                    cursor="pointer"
                    onClick={() => toggleMenu(item.label)}
                  >
                    <Box fontSize="20px">
                      {React.createElement(IconComponent)}
                    </Box>
                  </Box>
                </Tooltip>
              );
            }

            return (
              <Tooltip
                key={item.path}
                label={isCollapsed ? item.label : ""}
                placement="right"
                hasArrow
              >
                <Link
                  as={RouterLink}
                  to={item.path}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  bg={isActive(item.path || "") ? activeBg : "transparent"}
                  color={isActive(item.path || "") ? activeColor : "inherit"}
                  _hover={{ bg: activeBg }}
                  justifyContent={isCollapsed ? "center" : "flex-start"}
                >
                  <Box fontSize="20px">
                    {React.createElement(IconComponent)}
                  </Box>
                  {!isCollapsed && <Text ml={3}>{item.label}</Text>}
                  {!isCollapsed && item.path === "/restaurants" && pendingCount > 0 && (
                    <Badge ml="auto" colorScheme="orange" variant="solid" borderRadius="full" px={2}>
                      {pendingCount}
                    </Badge>
                  )}
                  {isCollapsed && item.path === "/restaurants" && pendingCount > 0 && (
                    <Box
                      position="absolute"
                      top="8px"
                      right="8px"
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg="orange.500"
                    />
                  )}
                </Link>
              </Tooltip>
            );
          })}
        </VStack>

        {/* Collapse Toggle Button */}
        <IconButton
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          size="sm"
          position="absolute"
          top="50%"
          right={isCollapsed ? "-12px" : "-12px"}
          transform="translateY(-50%)"
          bg={bgSidebar}
          border="1px"
          borderColor={borderColor}
          borderRadius="full"
          boxShadow="md"
          onClick={() => setIsCollapsed(!isCollapsed)}
          zIndex={10}
        >
          {React.createElement((isCollapsed ? FiChevronRight : FiChevronLeft) as React.ComponentType<any>)}
        </IconButton>
      </Box>

      {/* Main Content with Header */}
      <Box flex={1} bg={canvasBg} display="flex" flexDirection="column" minW={0}>
        {bannerItems.length > 0 && (
          <Box bg="orange.500" color="white" px={6} py={3}>
            <HStack align="flex-start" justify="space-between" spacing={4}>
              <Box>
                <Text fontWeight="700">Workstation approval required</Text>
                <VStack align="stretch" spacing={1} mt={1}>
                  {bannerItems.map((item) => (
                    <Link
                      key={item.id}
                      as={RouterLink}
                      to={`/restaurants/${item.storeId}?tab=station`}
                      color="white"
                      fontWeight="600"
                      textDecoration="underline"
                    >
                      {item.storeName} requested approval for {item.stationName}.
                    </Link>
                  ))}
                </VStack>
              </Box>
              <Button
                size="sm"
                variant="outline"
                color="white"
                borderColor="white"
                _hover={{ bg: "orange.600" }}
                onClick={dismissBanner}
                flexShrink={0}
              >
                Close
              </Button>
            </HStack>
          </Box>
        )}
        {/* Top Header with User Profile */}
        <Box
          bg={useColorModeValue("white", "gray.800")}
          borderBottom="1px"
          borderColor={borderColor}
          px={6}
          py={3}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {!isLoading && <UserProfileMenu />}
        </Box>

        {/* Page Content */}
        <Box flex={1} overflow="auto">
          {children}
        </Box>
      </Box>
    </HStack>
  );
};

export default Sidebar;

